import React, { useEffect, useRef, useState } from 'react';
import PlayerAvatar from '../common/PlayerAvatar';
import CompactRatingDisplay from '../common/CompactRatingDisplay';
import { TrophyIcon, ArrowUpIcon, ArrowDownIcon, MinusIcon } from '@heroicons/react/24/solid';
import { Player, PlayerWithLeagueContext } from '../../generated-api';

interface PlayerRatingImpactCardProps {
  player: Player | PlayerWithLeagueContext;
  position: number;
  totalPlayers: number;
  ratingBefore?: { rating: number; sigma: number };
  ratingAfter?: { rating: number; sigma: number };
  ratingChange?: number;
  winProbability?: number;
  ratingsReady: boolean;
  ratingsTimedOut?: boolean;
}

const POSITION_STYLES: Record<number, { badge: string; bg: string; border: string }> = {
  1: {
    badge: 'bg-amber-400 text-amber-900',
    bg: 'bg-gradient-to-br from-amber-50 to-yellow-50',
    border: 'border-amber-300',
  },
  2: {
    badge: 'bg-stone-300 text-stone-700',
    bg: 'bg-gradient-to-br from-stone-50 to-slate-50',
    border: 'border-stone-200',
  },
  3: {
    badge: 'bg-orange-700/20 text-orange-800',
    bg: 'bg-gradient-to-br from-orange-50 to-amber-50',
    border: 'border-orange-200',
  },
};

const PlayerRatingImpactCard: React.FC<PlayerRatingImpactCardProps> = ({
  player,
  position,
  totalPlayers,
  ratingBefore,
  ratingAfter,
  ratingChange,
  winProbability,
  ratingsReady,
  ratingsTimedOut,
}) => {
  const [displayDelta, setDisplayDelta] = useState(0);
  const animationRef = useRef<number | null>(null);
  const prefersReducedMotion = useRef(
    typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    if (!ratingsReady || ratingChange === undefined) {
      setDisplayDelta(0);
      return;
    }

    if (prefersReducedMotion.current) {
      setDisplayDelta(ratingChange);
      return;
    }

    const duration = 400;
    const startTime = performance.now();
    const targetValue = ratingChange;

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = progress < 0.5
        ? 2 * progress * progress
        : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      setDisplayDelta(Math.round(targetValue * eased * 100) / 100);

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      }
    };

    animationRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [ratingChange, ratingsReady]);

  const posStyle = POSITION_STYLES[position] || {
    badge: 'bg-surface-subtle text-body',
    bg: 'bg-surface',
    border: 'border-edge',
  };

  const deltaColor = (ratingChange ?? 0) > 0
    ? 'text-success-700'
    : (ratingChange ?? 0) < 0
    ? 'text-error-600'
    : 'text-body';

  const deltaSign = displayDelta > 0 ? '+' : displayDelta === 0 ? '+' : '';
  const isWinner = position === 1;
  const isLast = position === totalPlayers && totalPlayers > 1;

  // Badge logic
  // Unexpectedly good: 1st place with win prob < 1/N * 0.6
  // Unexpectedly bad: Last place with win prob > 1/N * 1.5
  const baselineProb = 100 / totalPlayers;
  const isUnexpectedlyGood = isWinner && winProbability !== undefined && winProbability < baselineProb * 0.6;
  const isUnexpectedlyBad = isLast && winProbability !== undefined && winProbability > baselineProb * 1.5;
  const noRatingData = ratingsReady && !ratingBefore && !ratingAfter;
  const notInLeague = noRatingData && !ratingsTimedOut;

  return (
    <div
      className={`rounded-xl border transition-all duration-300 ${
        notInLeague
          ? 'bg-surface-subtle border-edge opacity-60'
          : `${posStyle.border} ${posStyle.bg} ${isWinner ? 'border-2 shadow-sm' : ''}`
      }`}
    >
      <div className="p-4">
        {/* Row 1: Position + Avatar + Name (left) | Delta (right) */}
        <div className="flex items-center gap-3">
          {/* Position Badge */}
          <div className={`flex-shrink-0 w-8 h-8 rounded-full ${posStyle.badge} flex items-center justify-center text-sm font-bold`}>
            {isWinner ? (
              <TrophyIcon className="h-4 w-4" />
            ) : (
              position
            )}
          </div>

          {/* Avatar */}
          <div className="flex-shrink-0">
            <PlayerAvatar
              name={player.name}
              imageUrl={player.imageUrl}
              size="small"
            />
          </div>

          {/* Name + Winner label */}
          <div className="flex-1 min-w-0">
            <span className="font-semibold text-heading truncate block">{player.name}</span>
            <div className="flex flex-wrap gap-1 mt-0.5">
              {isWinner && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold uppercase tracking-wider">
                  Winner
                </span>
              )}
              {isUnexpectedlyGood && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-success-100 text-success-700 font-bold uppercase tracking-wider animate-pulse">
                  Unstoppable
                </span>
              )}
              {isUnexpectedlyBad && (
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-error-100 text-error-700 font-bold uppercase tracking-wider">
                  Rough Day
                </span>
              )}
            </div>
          </div>

          {/* Rating Delta */}
          <div className="flex-shrink-0 text-right">
            {ratingsReady ? (
              ratingChange !== undefined ? (
                <span className={`inline-flex items-center gap-0.5 text-xl font-bold font-mono ${deltaColor}`}>
                  {(ratingChange ?? 0) > 0 && <ArrowUpIcon className="h-4 w-4" />}
                  {(ratingChange ?? 0) < 0 && <ArrowDownIcon className="h-4 w-4" />}
                  {(ratingChange ?? 0) === 0 && <MinusIcon className="h-4 w-4" />}
                  {deltaSign}{displayDelta.toFixed(2)}
                </span>
              ) : null
            ) : (
              <div className="animate-pulse bg-surface-skeleton rounded h-7 w-16" />
            )}
          </div>
        </div>

        {/* Row 2: Ratings before → after + Win probability */}
        <div className="mt-2.5 pl-11 flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {ratingsReady ? (
              ratingBefore && ratingAfter ? (
                <>
                  <CompactRatingDisplay rating={ratingBefore.rating} sigma={ratingBefore.sigma} className="text-xs" />
                  <span className="text-muted text-xs">&rarr;</span>
                  <CompactRatingDisplay rating={ratingAfter.rating} sigma={ratingAfter.sigma} className="text-xs" />
                </>
              ) : ratingsTimedOut ? (
                <span className="text-xs text-muted italic">Rating pending…</span>
              ) : (
                <span className="text-xs text-muted italic">Not in this league</span>
              )
            ) : (
              <div className="flex gap-2">
                <div className="animate-pulse bg-surface-skeleton rounded-full h-5 w-24" />
                <div className="animate-pulse bg-surface-skeleton rounded-full h-5 w-24" />
              </div>
            )}
          </div>

          {winProbability !== undefined && (
            <span className="flex-shrink-0 text-xs text-muted">
              Expected: {Math.round(winProbability)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlayerRatingImpactCard;
